import { IPresenter } from '../../IPresenter';
import { ITransformer } from '../../ITransformer';

import { OzDate } from './oz-date/OzDate';

export enum DATE_FORMAT_CATEGORIES {
    YEAR,
    MONTH,
    DAY
}

export type IDateFormatToken<TCategory, TFormat> =  {
    category: TCategory;
    format: TFormat;
};

export type IDateFormatPart = (
    IDateFormatToken<DATE_FORMAT_CATEGORIES.YEAR, YEAR_FORMATS> |
    IDateFormatToken<DATE_FORMAT_CATEGORIES.MONTH, MONTH_FORMATS> |
    IDateFormatToken<DATE_FORMAT_CATEGORIES.DAY, DAY_FORMATS> |
    string
);

export type IDateFormat = IDateFormatPart[];

export enum YEAR_FORMATS {
    NUMBER,
}

export enum MONTH_FORMATS {
    SHORT
}

export enum DAY_FORMATS {
    NUMBER,
}

export type IDateLocalizer = {
    getYear (year: number, format: YEAR_FORMATS): string;
    getMonth (month: number, format: MONTH_FORMATS): string;
    getDay (day: number, format: DAY_FORMATS): string;
    getDateFormat (): IDateFormat;
};

type IDateParts = {
    year: number;
    month: number;
    day: number;
};

export type IDateTransformer = ITransformer<Date, OzDate>;

/**
 * Converts dates into strings that are ready to be displayed to end users.
 */
export class DatePresenter implements IPresenter<Date, string> {
    private _converter: IDateTransformer;
    private _localizer: IDateLocalizer;

    /**
     * @param transformer A transformer that dates should be passed through prior to being formatted.
     * @param localizer A localizer that will be used to generate a formatted date string.
     */
    public constructor (transformer: IDateTransformer, localizer: IDateLocalizer) {
        this._converter = transformer;
        this._localizer = localizer;
    }

    public present (input: Date): string {
        const localDate: OzDate = this._converter.transform(input);
        const dateParts = DatePresenter._getDateParts(localDate);

        return this._localizer
            .getDateFormat()
            .map((part: IDateFormatPart): string => {
                if (typeof part === 'string') {
                    return part;
                }

                return this._parseFormatPart(dateParts, part);
            }).join('');
    }

    private _parseFormatPart (dateParts: IDateParts, token: Exclude<IDateFormatPart, string>): string {
        if (token.category === DATE_FORMAT_CATEGORIES.YEAR) {
            return this._localizer.getYear(dateParts.year, token.format);
        }

        if (token.category === DATE_FORMAT_CATEGORIES.MONTH) {
            return this._localizer.getMonth(dateParts.month, token.format);
        }

        return this._localizer.getDay(dateParts.day, token.format);
    }

    private static _getDateParts (date: OzDate): IDateParts {
        return {
            year: date.getFullYear(),
            month: date.getMonth(),
            day: date.getDate(),
        };
    }
}
